/**
 * PathNote Design Tokens (v4)
 *
 * mockup-4tabs.png のビジュアル目標から抽出した、サービス全体の見た目を司る単一のソース。
 * 既存の inline style ベースの実装に少しずつ流用し、最終的には全UIをここから引くようにする。
 *
 * 設計原則:
 * - 4タブそれぞれに「テーマ色」を持たせる（自分ノート=青 / 自分の言葉=オレンジ / 自分ログ=緑 / マイページ=紫）
 * - 中立色（背景・テキスト・ボーダー）はテーマに依存せず共通
 * - 仮説形・優しいトーン: 角丸大きめ・薄シャドウ・低彩度のパステル
 * - 階層を spacing と font weight で表現し、装飾は最小限
 */

// ── Brand: 共通の中立カラー ─────────────────────────────────
// 思想：「全体はわずかに温かい空気感、クリーム色セクションが特別な温かさのアンカー」
// 疲れている時に開くサービスのため、静かで落ち着いた温かいトーン。
// 色階層: bg（温かいオフホワイト）< surface（より明るい温かい白）< クリーム（最も温かい）
const neutral = {
  bg:           "#FAF4ED", // ページ全体の背景（温かいピンク寄りのベージュ）
  surface:      "#FEFCF9", // メインカード背景（bg より明るく、ごく薄い温かみ）
  surfaceAlt:   "#F3EBE4", // 入力欄やセカンダリパネル（温かい薄ベージュ）
  border:       "#E8DDD6", // 通常のボーダー（温かいグレーベージュ）
  borderStrong: "#CCBFB7", // 強調ボーダー（フォーカスライン等）
};

// テキスト階層
const text = {
  primary:   "#1F2937", // 本文・見出し
  secondary: "#4B5563", // 補足・本文の弱め
  tertiary:  "#9CA3AF", // メタ・キャプション
  inverse:   "#FFFFFF", // ダーク面の上
};

// セマンティック（状態を表す色）
const semantic = {
  success:        "#3E8D63",
  successLight:   "#F0FBF5",
  successBorder:  "#BFE7D2",
  warning:        "#A87427",
  warningLight:   "#FFF8E5",
  warningBorder:  "#F5C97A",
  error:          "#C44C4C",
  errorLight:     "#FFEAEA",
  info:           "#5B6FA8",
  infoLight:      "#EEF2FF",
};

// ── アクセント（共通／タブ非依存） ────────────────────────────
// すべてのタブはこの単一アクセントを使用。タブ別カラーは廃止。
const accent = {
  primary:     "#C97B7B", // ウォームローズ（タブ選択中・アイコン・通常アクション）
  primaryDark: "#B06060",
  medium:      "#DEAEAE",
  light:       "#FDF0EF", // アイコン背景・薄い帯
  // 感情アクセント（暖色）— ヒーローカードのみ
  orange:      "#D4897B", // テラコッタオレンジ（落ち着いた暖色）
  orangeLight: "#FFF1ED",
  green:       "#8FB99A", // セージグリーン（植物・成長感）
  greenLight:  "#E8F2EC",
};

// ヒーローカード専用（感情の余韻を出す唯一の暖色面）
const hero = {
  gradient: "linear-gradient(180deg, #FFF5F4 0%, #FFFDFB 100%)",
  border:   "#F3C9C4",
};

// ── 単一テーマ（全タブ共通の baseTheme） ──────────────────────
// 旧 themes.{note,stock,log,page} は全て同じ baseTheme を返す。
// 既存コードが themeFor("stock") などで呼び出していても破壊的変更なしで済むよう、
// 同じカラー値を持つ4つのキーとして公開する。
const baseTheme = {
  primary:     accent.primary,
  primaryDark: accent.primaryDark,
  medium:      accent.medium,
  light:       accent.light,
  gradient:    `linear-gradient(135deg, ${accent.light}, ${neutral.surface})`,
  // ヒーローカード（暖色グラデ）もbaseThemeに含める
  heroGradient: hero.gradient,
  heroBorder:   hero.border,
};

const themes = {
  note:  { ...baseTheme, key:"note",  label:"自分ノート" },
  stock: { ...baseTheme, key:"stock", label:"自分の言葉" },
  log:   { ...baseTheme, key:"log",   label:"自分ログ" },
  page:  { ...baseTheme, key:"page",  label:"マイページ" },
};

// ── Typography ────────────────────────────────────────────
const fontFamily = {
  base: "'Zen Maru Gothic','Noto Sans JP','Hiragino Sans',sans-serif",
  mono: "'DM Mono',monospace",
};

// 数値はそのまま fontSize に使える px 値。
const fontSize = {
  xxs:     10, // ラベル・キャプション
  xs:      11, // メタ
  sm:      12, // 補足文
  base:    13, // 本文標準
  md:      14, // 本文強調・ボタン
  lg:      15, // カード本文
  xl:      16, // セクションタイトル
  h3:      17, // 小見出し
  h2:      19, // 中見出し
  h1:      22, // 大見出し
  display: 26, // ランディング・完了画面
};

const fontWeight = {
  regular:  400,
  medium:   500,
  semibold: 600,
  bold:     700,
};

const lineHeight = {
  tight:   1.4,  // 数値表示
  snug:    1.55, // 見出し
  normal:  1.7,  // メタ・補助文
  relaxed: 1.85, // 本文
  loose:   2.0,  // ふわっと読ませる長文
};

const letterSpacing = {
  tight:  "-0.025em",
  normal: "0",
  wide:   "0.04em", // ラベル
  wider:  "0.08em", // STEP表記
  widest: "0.12em", // MY PAGE的なセクション
};

// ── Spacing ───────────────────────────────────────────────
// 4px 基調のスケール。値は px。
const spacing = {
  none:  0,
  xxs:   2,
  xs:    4,
  sm:    6,
  base:  8,
  md:    10,
  lg:    12,
  xl:    14,
  "2xl": 16,
  "3xl": 18,
  "4xl": 20,
  "5xl": 24,
  "6xl": 28,
  "7xl": 32,
  "8xl": 40,
  "9xl": 48,
};

// ── Radius ────────────────────────────────────────────────
const radius = {
  sm:    8,
  md:    10,
  lg:    12,
  xl:    14,
  "2xl": 16,
  "3xl": 18,
  "4xl": 20,
  pill:  999,
  circle: "50%",
};

// ── Shadow ────────────────────────────────────────────────
// カード・セクションの影は温かみのある黄褐色ベース（冷たい青みを排除）。
// モーダル・CTA は従来通り（深みのあるニュートラル / アクセントブルー）。
const shadow = {
  none:      "none",
  sm:        "0 1px 6px rgba(100,80,50,0.07)",   // 温かい薄影（カード標準）
  md:        "0 4px 16px rgba(100,80,50,0.09)",   // 温かい中影（ホバー・強調）
  lg:        "0 8px 24px rgba(100,80,50,0.12)",   // 温かい大影（フローティング要素）
  xl:        "0 16px 40px rgba(31,41,55,0.16)",   // 深い影（モーダル背面）
  modal:     "0 18px 60px rgba(31,41,55,0.18)",   // モーダル本体
  cta:       "0 6px 24px rgba(201,123,123,0.30)",  // CTAボタン（ウォームローズ）
  insetSoft: "inset 0 1px 0 rgba(255,255,255,0.6)",
};

// ── Layout ────────────────────────────────────────────────
const layout = {
  navHeight:        52,
  contentMaxWidth:  600,
  pagePaddingX:     20,
  pagePaddingY:     24,
  pagePaddingBottom:80,
  cardPaddingX:     20,
  cardPaddingY:     20,
  cardGap:          14,
  tabBarHeight:     44,
};

// ── Animation ─────────────────────────────────────────────
const motion = {
  durationFast:   "0.18s",
  durationNormal: "0.25s",
  durationSlow:   "0.4s",
  ease:           "ease",
  easeOut:        "cubic-bezier(0.16,1,0.3,1)",
  spin:           "spin 0.8s linear infinite",
  fadeUp:         "fadeUp 0.3s ease",
};

// ── Component Recipes ─────────────────────────────────────
// 各UI要素のスタイルを「テーマを引数に取る関数」で組み立てる。
// 既存のinline styleで使えるよう、CSSプロパティの object を返す。

/** カード（基本面）：白地、薄ボーダー、薄シャドウ、十分な角丸 */
const card = ({ theme = themes.note, padding = "lg" } = {}) => ({
  background: neutral.surface,
  border: `1px solid ${neutral.border}`,
  borderRadius: radius["2xl"],
  padding:
    padding === "sm"  ? `${spacing.lg}px ${spacing["3xl"]}px` :
    padding === "lg"  ? `${spacing["3xl"]}px ${spacing["4xl"]}px` :
    /* xl */            `${spacing["6xl"]}px ${spacing["4xl"]}px`,
  boxShadow: shadow.sm,
  // 必要ならアクセントを彩る用にテーマ色をCSS変数で渡せる
  "--theme-primary": theme.primary,
});

/** ハイライトカード（テーマ色のグラデ面）：完了画面・サマリ・言葉の変化など */
const highlightCard = ({ theme = themes.note } = {}) => ({
  background: theme.gradient,
  border: `1px solid ${theme.medium}`,
  borderRadius: radius.xl,
  padding: `${spacing.lg}px ${spacing["3xl"]}px`,
  boxShadow: shadow.sm,
});

/** プライマリボタン：そのタブのテーマ色で塗り */
const buttonPrimary = ({ theme = themes.note, disabled = false, fullWidth = true } = {}) => ({
  width: fullWidth ? "100%" : "auto",
  padding: `${spacing.lg}px ${spacing["2xl"]}px`,
  background: disabled ? "#D1D5DB" : theme.primary,
  color: text.inverse,
  border: "none",
  borderRadius: radius.lg,
  fontSize: fontSize.md,
  fontWeight: fontWeight.semibold,
  fontFamily: fontFamily.base,
  cursor: disabled ? "not-allowed" : "pointer",
  boxShadow: disabled ? "none" : shadow.cta,
  transition: `all ${motion.durationFast} ${motion.ease}`,
});

/** セカンダリボタン：白地・テーマカラー文字 */
const buttonSecondary = ({ theme = themes.note, fullWidth = true } = {}) => ({
  width: fullWidth ? "100%" : "auto",
  padding: `${spacing.lg}px ${spacing["2xl"]}px`,
  background: neutral.surface,
  color: theme.primary,
  border: `1px solid ${theme.medium}55`,
  borderRadius: radius.lg,
  fontSize: fontSize.base,
  fontWeight: fontWeight.semibold,
  fontFamily: fontFamily.base,
  cursor: "pointer",
});

/** ゴーストボタン：戻る・スキップ・キャンセル */
const buttonGhost = ({ fullWidth = true } = {}) => ({
  width: fullWidth ? "100%" : "auto",
  padding: `${spacing.lg}px ${spacing["2xl"]}px`,
  background: "transparent",
  color: text.tertiary,
  border: "none",
  borderRadius: radius.lg,
  fontSize: fontSize.base,
  fontWeight: fontWeight.medium,
  fontFamily: fontFamily.base,
  cursor: "pointer",
});

/** チップ（選んだ言葉・タグ）：白背景 + テーマ色の細いボーダー。
 *  「白の空気感、感情だけ暖色」方針に沿って、面塗りは控えめにし、色は線とテキストで示す。
 *  selected=true のときだけ反転（テーマ色面 + 白文字）して、選択状態を明示する。 */
const chip = ({ theme = themes.note, selected = false } = {}) => ({
  display: "inline-block",
  padding: `${spacing.sm}px ${spacing.lg}px`,
  background: selected ? theme.primary : neutral.surface,
  color: selected ? text.inverse : theme.primary,
  border: `1px solid ${selected ? theme.primary : theme.primary}`,
  borderRadius: radius.lg,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.semibold,
  lineHeight: lineHeight.snug,
});

/** バッジ（NEW / 件数 / メタ） */
const badge = ({ tone = "primary", theme = themes.note } = {}) => {
  const map = {
    primary: { bg: theme.primary,        fg: text.inverse },
    soft:    { bg: theme.light,          fg: theme.primary },
    success: { bg: semantic.successLight, fg: semantic.success },
    warning: { bg: semantic.warningLight, fg: semantic.warning },
    muted:   { bg: neutral.surfaceAlt,    fg: text.tertiary },
  };
  const { bg, fg } = map[tone] || map.primary;
  return {
    display: "inline-block",
    padding: `${spacing.xxs}px ${spacing.base}px`,
    background: bg,
    color: fg,
    borderRadius: radius.pill,
    fontSize: fontSize.xxs,
    fontWeight: fontWeight.semibold,
    letterSpacing: letterSpacing.wide,
    lineHeight: lineHeight.tight,
  };
};

/** 入力欄（textarea/input共通の基本形） */
const inputBase = () => ({
  width: "100%",
  boxSizing: "border-box",
  padding: `${spacing.lg}px ${spacing.xl}px`,
  border: `1.5px solid ${neutral.border}`,
  borderRadius: radius.lg,
  background: neutral.surfaceAlt,
  color: text.primary,
  fontSize: fontSize.lg,
  lineHeight: lineHeight.relaxed,
  fontFamily: fontFamily.base,
  outline: "none",
  transition: `border-color ${motion.durationFast} ${motion.ease}`,
});

/** モーダル背面のオーバーレイ */
const overlay = () => ({
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.36)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: spacing["4xl"],
});

/** モーダル本体（カード） */
const dialog = () => ({
  background: neutral.surface,
  border: `1px solid ${neutral.border}`,
  borderRadius: radius["3xl"],
  padding: `${spacing["5xl"]}px ${spacing["4xl"]}px`,
  width: "100%",
  maxWidth: 400,
  boxShadow: shadow.modal,
});

// ── 互換レイヤ（既存App.jsのCショートカット） ────────────────
// 既存コードが C.bg / C.accent などで参照しているので、徐々に移行できるよう
// 同じキーで neutral + 任意のテーマを返すヘルパも用意。
const legacyC = (theme = themes.note) => ({
  bg:       neutral.bg,
  surface:  neutral.surface,
  border:   neutral.border,
  accent:   theme.primary,
  accentL:  theme.light,
  accentM:  theme.medium,
  text:     text.primary,
  sub:      text.secondary,
  muted:    text.tertiary,
  warm:     themes.stock.primary,
  warmL:    themes.stock.light,
  shadow:   shadow.sm,
});

// ── Public API ────────────────────────────────────────────
export const tokens = {
  neutral,
  text,
  semantic,
  accent,        // 共通アクセント（タブ非依存）
  hero,          // ヒーローカード専用（暖色グラデ）
  themes,        // 後方互換: 全タブ同じ baseTheme を返す
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  spacing,
  radius,
  shadow,
  layout,
  motion,
};

export const components = {
  card,
  highlightCard,
  buttonPrimary,
  buttonSecondary,
  buttonGhost,
  chip,
  badge,
  inputBase,
  overlay,
  dialog,
};

export const themesByTab = {
  note:  themes.note,
  stock: themes.stock,
  log:   themes.log,
  page:  themes.page,
};

/** 現在のタブIDからテーマを取得（不明値のときは note にフォールバック） */
export const themeFor = (tabId) => themesByTab[tabId] || themes.note;

export { legacyC };

const designTokens = { tokens, components, themesByTab, themeFor, legacyC };
export default designTokens;
